/**
 * ThemePicker — a modal to choose among the 5 named themes with a color swatch
 * next to each name. Works the same everywhere (main shell + inside the editor,
 * which previously only toggled light/dark). Driven by useThemeStore.setThemeName
 * (which also syncs `mode`), so picking a dark-family theme flips dark mode too.
 *
 * "Set as my default" (checked by default) controls whether the choice sticks:
 * pick themes to preview live; if you uncheck it, closing reverts to what you
 * had — so you can try a theme without committing.
 */
import { useEffect, useRef, useState } from "react";
import { testIds } from "@/testIds";
import { IconCheck } from "@tabler/icons-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/lilia-ui/dialog";
import { useThemeStore } from "@/store/themeStore";
import {
  ACCENT_NAMES,
  ACCENT_LABELS,
  ACCENT_BLURB,
  ACCENT_SWATCH,
} from "@/utils/accent";
import { PaneSurfaceTabs } from "@/components/editor/ArtifactSurfaceTabs";
import {
  DEFAULT_PANES_KEY,
  readDefaultPanes,
  selectSurface,
  serialisePanes,
  type PaneLayout,
  type PaneSide,
  type PaneSurface,
} from "@/lib/workspacePanes";
import { THEME_LABELS, type ThemeName } from "@/utils/themeVariant";
import { cn } from "@/lib/cn";

// Two themes. Dim / Forest / Sepia were deleted on 14 September 2026 — they
// had been excluded from this list for long enough that no user had seen them,
// while still costing 90 variable overrides to maintain.
const PICKER_THEMES: ThemeName[] = ["light", "dark"];

// Representative swatch per theme: page background + accent. Reads as a mini
// preview of the theme so users "choose correctly" at a glance.
const THEME_SWATCH: Record<ThemeName, { bg: string; accent: string }> = {
  // The dot takes the LIVE accent. It was #0f6cbd / #2899f5 — neither of
  // which the product ever painted, so the swatch advertised a colour that
  // did not exist. Two values claiming one role is why the chrome read as
  // assembled rather than designed.
  light: { bg: "#dfe7f1", accent: "var(--lilia-primary)" },
  dark: { bg: "#1b1b1b", accent: "var(--lilia-primary)" },
};

const THEME_BLURB: Record<ThemeName, string> = {
  light: "Cool, default",
  dark: "Calm near-black",
};

interface ThemePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ThemePicker({ open, onOpenChange }: ThemePickerProps) {
  const themeName = useThemeStore((s) => s.themeName) ?? "light";
  const setThemeName = useThemeStore((s) => s.setThemeName);
  const accent = useThemeStore((s) => s.accent) ?? "cerulean";
  const setAccent = useThemeStore((s) => s.setAccent);

  const original = useRef<ThemeName>(themeName);
  const [keepDefault, setKeepDefault] = useState(true);

  /**
   * The arrangement every document opens with.
   *
   * Uses the workspace's own strip, so choosing here behaves exactly as it does
   * in the workspace — including the swap when you pick what the other side is
   * already holding. A second control with its own rules would be a second
   * thing to learn for the same decision.
   */
  const [defaultPanes, setDefaultPanes] = useState<PaneLayout>(
    () => readDefaultPanes((k) => localStorage.getItem(k)),
  );
  const chooseDefault = (side: PaneSide, surface: PaneSurface) => {
    const next = selectSurface(defaultPanes, side, surface);
    setDefaultPanes(next);
    try { localStorage.setItem(DEFAULT_PANES_KEY, serialisePanes(next)); } catch { /* ignore */ }
  };

  // Snapshot the current theme each time the picker opens, and reset the
  // "set as default" checkbox to on.
  useEffect(() => {
    if (open) {
      original.current = themeName;
      setKeepDefault(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (next: boolean) => {
    if (!next && !keepDefault) {
      // Revert the live preview. Only the theme previews live now — the
      // panel-background tint was removed on 14 September 2026.
      if (original.current !== themeName) setThemeName(original.current);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="theme-picker" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Choose a theme</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="theme-picker-options">
          {PICKER_THEMES.map((name) => {
            const sw = THEME_SWATCH[name];
            const active = name === themeName;
            return (
              <button
                key={name}
                type="button"
                data-testid="theme-picker-option"
                data-theme-name={name}
                data-active={active}
                onClick={() => setThemeName(name)}
                className={cn(
                  "flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors",
                  active
                    ? "border-[var(--lilia-primary,#0A76A4)] bg-[var(--lilia-primary-subtle,#0A76A414)]"
                    : "border-[var(--lilia-border,#e2e8f0)] hover:bg-[var(--lilia-hover,#f1f5f9)]",
                )}
              >
                {/* Swatch: theme bg with an accent dot */}
                <span
                  className="relative h-8 w-8 shrink-0 rounded-md border border-black/10 shadow-inner"
                  style={{ background: sw.bg }}
                  aria-hidden
                >
                  <span
                    className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full"
                    style={{ background: sw.accent }}
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[13.5px] font-semibold text-[var(--lilia-text,#0f172a)]">
                      {THEME_LABELS[name]}
                    </span>
                    {active && <IconCheck size={14} className="text-[var(--lilia-primary,#0A76A4)]" />}
                  </span>
                  <span className="block truncate text-[11.5px] text-[var(--lilia-text-muted,#64748b)]">
                    {THEME_BLURB[name]}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {/*
          Accents. Three values on two themes, all six contrast-verified
          (e2e/scripts/contrast-audit.mjs). This is the axis the deleted
          dim/forest/sepia themes were reaching for: someone choosing Sepia
          wanted a warmer product, not a warmer ground.
        */}
        <div data-testid={testIds.editor.accentOptions}>
          <div className="mb-1.5 mt-4 text-[13px] font-semibold text-[var(--lilia-text,#0f172a)]">
            Accent
          </div>
          <div className="grid grid-cols-3 gap-2">
            {ACCENT_NAMES.map((name) => {
              const active = name === accent;
              return (
                <button
                  key={name}
                  type="button"
                  data-testid={testIds.editor.accentOption}
                  data-accent-name={name}
                  data-active={active}
                  onClick={() => setAccent(name)}
                  className={cn(
                    "flex items-center gap-2 rounded-lg border p-2 text-left transition-colors",
                    active
                      ? "border-[var(--lilia-primary)] bg-[var(--lilia-primary-subtle)]"
                      : "border-[var(--lilia-border-strong)] hover:bg-[var(--lilia-hover)]",
                  )}
                >
                  <span
                    className="h-5 w-5 shrink-0 rounded-full border border-black/10"
                    style={{ background: ACCENT_SWATCH[name] }}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1">
                      <span className="truncate text-[12.5px] font-medium text-[var(--lilia-text)]">
                        {ACCENT_LABELS[name]}
                      </span>
                      {active && <IconCheck size={13} className="text-[var(--lilia-primary)]" />}
                    </span>
                    <span className="block truncate text-[11px] text-[var(--lilia-text-muted)]">
                      {ACCENT_BLURB[name]}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Where the workspace starts. Separate from the live arrangement on
            purpose: moving a surface is for the session you move it in, this is
            what every document opens with. */}
        <div data-testid="default-panes-options">
          <div className="mb-1 text-[13px] font-semibold text-[var(--lilia-text,#0f172a)]">
            Default view
          </div>
          <div className="mb-2 text-[11.5px] text-[var(--lilia-text-muted,#64748b)]">
            What every document opens with. You can still switch either pane while you work.
          </div>
          {(["left", "right"] as const).map((side) => (
            <div key={side} className="mb-2 flex items-center gap-2">
              <span className="w-[86px] shrink-0 text-[12.5px] capitalize text-[var(--lilia-text-muted,#64748b)]">
                {side}
              </span>
              <PaneSurfaceTabs
                side={side}
                value={defaultPanes[side]}
                onChange={(next) => chooseDefault(side, next)}
              />
            </div>
          ))}
        </div>

        <label
          data-testid="theme-picker-keep-default"
        >
          <input
            type="checkbox"
            checked={keepDefault}
            onChange={(e) => setKeepDefault(e.target.checked)}
            className="h-4 w-4 rounded border-[var(--lilia-border,#cbd5e1)] accent-[var(--lilia-primary,#0A76A4)]"
          />
          Set as my default theme
          <span className="text-[11.5px] text-[var(--lilia-text-muted,#64748b)]">
            (uncheck to try without keeping)
          </span>
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            data-testid="theme-picker-done"
            onClick={() => close(false)}
            className="rounded-md bg-[var(--lilia-primary,#0A76A4)] px-4 py-1.5 text-[13px] font-medium text-[color:var(--lilia-on-primary)] hover:opacity-90"
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
